import { AvailableModules } from 'generated/featureDefinitions'

import i18n from 'modules/core/lib/i18n'
import type { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'

export type TaskCategory =
  | 'core'
  | 'time_tracking'
  | 'shift_management'
  | 'time_off'
  | 'projects'
  | 'documents'
  | 'workflows'
  | 'permissions_and_security'
  | 'expenses'
  | 'procurement'
  | 'compensation'
  | 'recruitment'
  | 'training'
  | 'performance'
  | 'engagement'
  | 'benefits'

export type Task = {
  taskKey: string
  title: string
  aiQuestion: string
  description: string
  tourId: string | null
  helpCenterUrl: string | null
  videoUrl: string | null
  mandatory: boolean
}

export type Category = {
  id: TaskCategory
  title: string
  moduleName: AvailableModules | string | null
  tasks: Task[]
}

export const CORE_CATEGORY_ID = 'core'

export const getCategories = (): Category[] => getStandardCategories()

export const getTaskActionUrl = (
  taskKey: string,
  navigation: Navigation<NavigationPaths>
): string | null => {
  switch (taskKey) {
    // Core
    case 'create_legal_entity':
      return navigation.settings.details.fullPath()
    case 'workplaces_and_public_holidays':
      return navigation.settings.locations.fullPath()
    case 'add_employee':
      return navigation.employees.list.fullPath()
    case 'create_job_position':
      return navigation.employees.jobCatalog.fullPath()
    case 'manage_contracts':
      return navigation.employees.list.fullPath()
    case 'view_organizational_chart':
      return navigation.employees.organizationChart.fullPath()
    case 'custom_fields_and_tables':
      return navigation.settings.customizations.fullPath()
    case 'create_teams':
      return navigation.employees.teams.fullPath()
    case 'basic_employee_data':
      return navigation.employees.list.fullPath()
    case 'customize_company_workspace':
      return navigation.settings.customizations.fullPath()
    // Time tracking
    case 'create_time_tracking_policy':
    case 'configure_time_tracking_tolerance':
    case 'configure_extra_time_compensation':
      return navigation.settings.time_tracking.fullPath()
    case 'create_breaks':
      return navigation.settings.time_categories_settings.fullPath()
    case 'create_work_schedules':
      return navigation.settings.workSchedule.fullPath()
    case 'special_hours':
      return navigation.settings.time_tracking.fullPath()
    case 'export_time_tracking_reports':
      return navigation.timeTracking.list.fullPath()
    // Time off
    case 'create_time_off_policy':
    case 'configure_fixed_balance_allowance':
      return navigation.settings.timeoffPolicies.fullPath()
    case 'create_absence_types':
      return navigation.settings.timeoffPolicies.absenceTypes.fullPath()
    case 'create_time_off_approval':
      return navigation.settings.timeoffPolicies.approvalGroups.fullPath()
    case 'create_allowance_adjustments':
      return navigation.employees.fullPath()
    case 'set_up_blocked_periods':
      return navigation.settings.timeoffPolicies.blockedPeriods.fullPath()
    case 'export_time_off_report':
      return navigation.employees.list.fullPath()
    // Documents
    case 'configure_documents_settings':
      return navigation.files.companyFiles.fullPath()
    case 'create_document_templates':
      return navigation.files.documentTemplates.fullPath()
    case 'configure_fillable_pdfs':
      return navigation.files.documentTemplates.fullPath()
    case 'e_signature':
      return navigation.documents.list.fullPath()
    case 'send_documents_in_bulk':
      return navigation.files.bulkFiles.fullPath()
    case 'upload_public_and_internal_documents':
      return navigation.files.companyFiles.fullPath()
    // Permissions & Security
    case 'configure_permissions':
      return navigation.settings.permissions.fullPath()
    case 'configure_trust_channel':
      return navigation.settings.complaints.fullPath()
    // Projects
    case 'create_project':
    case 'explore_project_management':
      return navigation.projects.list.fullPath()
    case 'view_export_project_data':
      return navigation.projects.list.fullPath()
    case 'flexible_project_tracking':
      return navigation.myProjects.fullPath()
    // Shifts management
    case 'create_shifts':
    case 'edit_delete_shifts':
    case 'configure_shifts_coverage':
      return navigation.shifts.fullPath()
    case 'create_rotating_shifts':
      return navigation.shifts.templates.fullPath()
    case 'configure_saved_shifts':
      return navigation.shifts.savedShifts.fullPath()
    case 'export_planned_shifts':
      return navigation.shifts.fullPath()
    // Workflows
    case 'create_workflow':
      return navigation.processes.fullPath()
    case 'explore_onboarding_space':
      return navigation.employees.list.fullPath()
    // Benefits
    case 'creating_stripe_bank_account_and_transferring_funds':
      return navigation.finance.cards.fullPath()
    case 'requesting_factorial_virtual_cards':
      return navigation.finance.cards.fullPath()
    // Expenses
    case 'add_expense':
      return navigation.expenses.list.fullPath()
    case 'setup_expense_approval':
      return navigation.settings.expenses.fullPath()
    case 'configure_expense_form':
      return navigation.settings.expenses.fullPath()
    // Procurement
    case 'configure_accounts_payable':
    case 'add_and_import_invoices':
    case 'pay_invoices_and_reconcile_transactions':
    case 'chart_of_accounts_and_general_ledger':
      return navigation.finance.invoices.fullPath()
    case 'add_bank_accounts':
      return navigation.finance.treasury.banking.accounts.fullPath()
    case 'import_vendors_and_link_accounts':
      return navigation.finance.spending.vendors.fullPath()
    // Recruitment
    case 'create_job_opening':
      return navigation.ats.jobs.fullPath()
    case 'configure_company_page':
      return navigation.settings.company_page.fullPath()
    case 'explore_recruitment':
      return navigation.ats.jobs.fullPath()
    case 'candidate_database':
      return navigation.ats.candidates.fullPath()
    case 'import_job_board_contracts':
      return navigation.settings.ats.integrations.fullPath()
    // TODO: Training
    // TODO: Performance
    // TODO: Engagement
    default:
      return null
  }
}

const getStandardCategories = (): Category[] => [
  {
    id: 'core',
    title: i18n.t('growth.get_started.tours_content.modules.core'),
    moduleName: null,
    tasks: [
      {
        taskKey: 'create_legal_entity',
        title: i18n.t('growth.get_started.tours_content.tasks.create_legal_entity.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.create_legal_entity.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_legal_entity.description'
        ),
        tourId: 'create_legal_entity',
        helpCenterUrl:
          'https://help.factorialhr.com/managing-my-company-account/about-legal-entities',
        videoUrl:
          'https://drive.google.com/file/d/10HnUdMiKnsVSvei535ZOH3PPd5a00KWI/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'workplaces_and_public_holidays',
        title: i18n.t('growth.get_started.tours_content.tasks.add_public_holiday.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.add_public_holiday.ai_question'),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.add_public_holiday.description'
        ),
        tourId: 'workplaces_and_public_holidays',
        helpCenterUrl: 'https://help.factorialhr.com/managing-my-company-account/about-workplaces',
        videoUrl:
          'https://drive.google.com/file/d/1OFfKTLojUN5iZ36MiuvyC3-ZFuMESxv3/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'add_employee',
        title: i18n.t('growth.get_started.tours_content.tasks.add_employee.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.add_employee.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.add_employee.description'),
        tourId: 'add_employee',
        helpCenterUrl:
          'https://help.factorialhr.com/managing-my-company-account/how-to-create-an-employee',
        videoUrl:
          'https://drive.google.com/file/d/1kWkMg0JXCdsxG7fL2ydD7UVqGseopN3u/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'create_job_position',
        title: i18n.t('growth.get_started.tours_content.tasks.create_job_position.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.create_job_position.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_job_position.description'
        ),
        tourId: 'create_job_position',
        helpCenterUrl:
          'https://help.factorialhr.com/teams-jobs-organizational-chart/about-the-job-catalog',
        videoUrl:
          'https://drive.google.com/file/d/1IHfwMzRCo8ujKba4ZCapLvhkTQyavDU0/view?usp=drive_link',
        mandatory: true,
      },
      {
        taskKey: 'manage_contracts',
        title: i18n.t('growth.get_started.tours_content.tasks.manage_contracts.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.manage_contracts.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.manage_contracts.description'),
        tourId: 'manage_contracts',
        helpCenterUrl:
          'https://help.factorialhr.com/contracts-terminations/how-to-manage-contracts',
        videoUrl:
          'https://drive.google.com/file/d/1qsriiJVewXDcT1vcL05Yw7ZOe02WC-oQ/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'custom_fields_and_tables',
        title: i18n.t('growth.get_started.tours_content.tasks.custom_fields_and_tables.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.custom_fields_and_tables.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.custom_fields_and_tables.description'
        ),
        tourId: 'custom_fields_and_tables',
        helpCenterUrl:
          'https://help.factorialhr.com/managing-my-company-account/about-custom-fields',
        videoUrl:
          'https://drive.google.com/file/d/110v-DsYHvoK6z8CuEkiaQ9Jakyr5A444/view?usp=drive_link',
        mandatory: false,
      },
      {
        taskKey: 'create_teams',
        title: i18n.t('growth.get_started.tours_content.tasks.create_teams.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.create_teams.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.create_teams.description'),
        tourId: 'create_teams',
        helpCenterUrl: 'https://help.factorialhr.com/en_US/sobre-la-funcionalidad-de-equipos',
        videoUrl:
          'https://drive.google.com/file/d/1WQsAXMHsbp3X9upiu2Z2twGjq02Wgm0J/view?usp=drive_link',
        mandatory: false,
      },
      {
        taskKey: 'basic_employee_data',
        title: i18n.t('growth.get_started.tours_content.tasks.basic_employee_data.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.basic_employee_data.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.basic_employee_data.description'
        ),
        tourId: 'basic_employee_data',
        helpCenterUrl:
          'https://help.factorialhr.com/managing-my-company-account/how-to-create-an-employee?from_search=190681790',
        videoUrl:
          'https://drive.google.com/file/d/1E7Y8anx0PKGayWa0pAOKRudEM0obbNeW/view?usp=sharing',
        mandatory: false,
      },
      {
        taskKey: 'customize_company_workspace',
        title: i18n.t('growth.get_started.tours_content.tasks.customize_company_workspace.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.customize_company_workspace.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.customize_company_workspace.description'
        ),
        tourId: 'customize_company_workspace',
        helpCenterUrl:
          'https://help.factorialhr.com/en_US/c%C3%B3mo-personalizar-el-espacio-de-trabajo-de-tu-empresa',
        videoUrl:
          'https://drive.google.com/file/d/1b9sCzKYVurVsPigoVoR7bvOoWMcqa1WR/view?usp=sharing',
        mandatory: false,
      },
      {
        taskKey: 'view_organizational_chart',
        title: i18n.t('growth.get_started.tours_content.tasks.view_organizational_chart.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.view_organizational_chart.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.view_organizational_chart.description'
        ),
        tourId: 'view_organizational_chart',
        helpCenterUrl:
          'https://help.factorialhr.com/teams-jobs-organizational-chart/about-the-organizational-chart',
        videoUrl:
          'https://drive.google.com/file/d/1rYVtUB9KsDPK7U8O2oknIsb-7Ktj0qZU/view?usp=sharing',
        mandatory: false,
      },
    ],
  },
  {
    id: 'time_tracking',
    title: i18n.t('growth.get_started.tours_content.modules.time_tracking'),
    moduleName: AvailableModules.time_tracking,
    tasks: [
      {
        taskKey: 'create_time_tracking_policy',
        title: i18n.t('growth.get_started.tours_content.tasks.create_time_tracking_policy.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.create_time_tracking_policy.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_time_tracking_policy.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/time-tracking/about-time-tracking-policies',
        videoUrl:
          'https://drive.google.com/file/d/1snLAdPdkjCWxOf8mPQlSp-s0qU3UDTgX/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'configure_time_tracking_tolerance',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.configure_time_tracking_tolerance.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.configure_time_tracking_tolerance.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.configure_time_tracking_tolerance.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/time-tracking/about-time-tracking-tolerance',
        videoUrl:
          'https://drive.google.com/file/d/1nob9tfKAu1IH2MLX6EXl3K0yd5RtTlGA/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'configure_extra_time_compensation',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.configure_extra_time_compensation.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.configure_extra_time_compensation.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.configure_extra_time_compensation.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/time-tracking/how-to-configure-extra-hour-compensation',
        videoUrl:
          'https://drive.google.com/file/d/1-glCgNMP1VenYMqxM5pKXGSyPP0VL_56/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'create_breaks',
        title: i18n.t('growth.get_started.tours_content.tasks.create_breaks.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.create_breaks.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.create_breaks.description'),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/time-tracking/how-to-configure-breaks',
        videoUrl:
          'https://drive.google.com/file/d/1WIIOygUkA01FB04udaKtVdTiPBfhfOA1/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'create_work_schedules',
        title: i18n.t('growth.get_started.tours_content.tasks.create_work_schedules.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.create_work_schedules.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_work_schedules.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/time-tracking/how-to-create-and-assign-work-schedules',
        videoUrl:
          'https://drive.google.com/file/d/1WIiY9yQkW9HUxQ37G10Q2PID_-5kMSVY/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'special_hours',
        title: i18n.t('growth.get_started.tours_content.tasks.special_hours.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.special_hours.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.special_hours.description'),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/control-horario/special-hours',
        videoUrl:
          'https://drive.google.com/file/d/1gh9qd0Yz7aSF6jnY2WxSdWo2W4wwOQd3/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'export_time_tracking_reports',
        title: i18n.t('growth.get_started.tours_content.tasks.export_time_tracking_reports.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.export_time_tracking_reports.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.export_time_tracking_reports.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/control-horario/como-exportar-informes-de-control-horario?from_search=147377447',
        videoUrl:
          'https://drive.google.com/file/d/1WIrfM7n0pT4xCaQZROD_EwSVyUj-EUz7/view?usp=sharing',
        mandatory: false,
      },
    ],
  },
  {
    id: 'time_off',
    title: i18n.t('growth.get_started.tours_content.modules.time_off'),
    moduleName: AvailableModules.timeoff,
    tasks: [
      {
        taskKey: 'create_absence_types',
        title: i18n.t('growth.get_started.tours_content.tasks.create_absence_types.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.create_absence_types.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_absence_types.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/absences-approvals/how-to-create-absence-types-2',
        videoUrl:
          'https://drive.google.com/file/d/18LBdN83LFGKRMmwphr4orJjAMgB5oB-B/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'create_time_off_policy',
        title: i18n.t('growth.get_started.tours_content.tasks.create_time_off_policy.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.create_time_off_policy.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_time_off_policy.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/time-off-settings/how-to-create-and-assign-time-off-policies',
        videoUrl:
          'https://drive.google.com/file/d/1tlIgdKiEF-U_5TPOBSxn18fwC5i9ykB1/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'configure_fixed_balance_allowance',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.configure_fixed_balance_allowance.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.configure_fixed_balance_allowance.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.configure_fixed_balance_allowance.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/time-off-settings/how-to-configure-fixed-balance-time-off-allowance',
        videoUrl:
          'https://drive.google.com/file/d/1EDWDytMczvWbuNurhMIq32_GUr9oQhkA/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'create_time_off_approval',
        title: i18n.t('growth.get_started.tours_content.tasks.create_time_off_approval.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.create_time_off_approval.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_time_off_approval.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/absences-approvals/how-to-create-time-off-approval-systems',
        videoUrl:
          'https://drive.google.com/file/d/10TNVeDbutVGszrZXvlEvf79gzdBmVcxG/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'create_allowance_adjustments',
        title: i18n.t('growth.get_started.tours_content.tasks.create_allowance_adjustments.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.create_allowance_adjustments.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_allowance_adjustments.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/absences-approvals/how-to-create-time-off-allowance-adjustments',
        videoUrl:
          'https://drive.google.com/file/d/1yiGXU3k64Ckx2xdBbjwS4TZRKyiMeovM/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'set_up_blocked_periods',
        title: i18n.t('growth.get_started.tours_content.tasks.set_up_blocked_periods.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.set_up_blocked_periods.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.set_up_blocked_periods.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/ausencias-y-aprobaciones/como-configurar-periodos-bloqueados-para-las-ausencias?from_search=147376799',
        videoUrl:
          'https://drive.google.com/file/d/1tpoDSTQsKnIWN5uvoOkte9pvzx5lHdee/view?usp=sharing',
        mandatory: false,
      },
      {
        taskKey: 'export_time_off_report',
        title: i18n.t('growth.get_started.tours_content.tasks.export_time_off_report.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.export_time_off_report.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.export_time_off_report.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/ausencias-y-aprobaciones/c%C3%B3mo-exportar-el-reporte-de-ausencias-de-tus-empleadosas?from_search=147376516',
        videoUrl:
          'https://drive.google.com/file/d/1PnIKdiD4ZFYpNSX__KqArXSBWm3gjrIn/view?usp=sharing',
        mandatory: false,
      },
    ],
  },
  {
    id: 'documents',
    title: i18n.t('growth.get_started.tours_content.modules.documents'),
    moduleName: AvailableModules.documents,
    tasks: [
      {
        taskKey: 'configure_documents_settings',
        title: i18n.t('growth.get_started.tours_content.tasks.configure_documents_settings.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.configure_documents_settings.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.configure_documents_settings.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/documents-management/about-the-documents-setting-page',
        videoUrl:
          'https://drive.google.com/file/d/18HdWnKv1haIUR7947EQn_I_6BsN6ksJw/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'create_document_templates',
        title: i18n.t('growth.get_started.tours_content.tasks.create_document_templates.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.create_document_templates.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_document_templates.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/document-templates/about-document-templates',
        videoUrl:
          'https://drive.google.com/file/d/1ngR4i2BCW7yiW6J_phzpi8eQsmXs7-lD/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'configure_fillable_pdfs',
        title: i18n.t('growth.get_started.tours_content.tasks.configure_fillable_pdfs.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.configure_fillable_pdfs.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.configure_fillable_pdfs.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/document-templates/about-fillable-pdf%E2%80%99s',
        videoUrl:
          'https://drive.google.com/file/d/1XSVFj65XTC4t4wwG0g1pJk0gN9CcNhGt/view?usp=sharing',
        mandatory: false,
      },
      {
        taskKey: 'e_signature',
        title: i18n.t('growth.get_started.tours_content.tasks.e_signature.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.e_signature.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.e_signature.description'),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/es_ES/about-the-e-signature',
        videoUrl:
          'https://drive.google.com/file/d/1ifSqfNkjrE3HE6edmBePzLJkTcPpsZHm/view?usp=sharing',
        mandatory: false,
      },
      {
        taskKey: 'send_documents_in_bulk',
        title: i18n.t('growth.get_started.tours_content.tasks.send_documents_in_bulk.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.send_documents_in_bulk.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.send_documents_in_bulk.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/es_ES/about-documents-in-bulk',
        videoUrl:
          'https://drive.google.com/file/d/1qneho4aNeS11rTGP7yPHxPJYcw2CRHKh/view?usp=sharing',
        mandatory: false,
      },
      {
        taskKey: 'upload_public_and_internal_documents',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.upload_public_and_internal_documents.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.upload_public_and_internal_documents.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.upload_public_and_internal_documents.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/es_ES/about-documents',
        videoUrl:
          'https://drive.google.com/file/d/1lVmO0zWEEJN0WJtlEC9twtkNp42oSv4r/view?usp=sharing',
        mandatory: false,
      },
    ],
  },
  {
    id: 'permissions_and_security',
    title: i18n.t('growth.get_started.tours_content.modules.permissions_and_security'),
    moduleName: null,
    tasks: [
      {
        taskKey: 'configure_permissions',
        title: i18n.t('growth.get_started.tours_content.tasks.configure_permissions.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.configure_permissions.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.configure_permissions.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/permission-groups-levels/about-permissions',
        videoUrl:
          'https://drive.google.com/file/d/1pyQ5dc4ZAUUIdaf32lNefOogPJzg-ist/view?usp=drive_link',
        mandatory: true,
      },
      {
        taskKey: 'configure_trust_channel',
        title: i18n.t('growth.get_started.tours_content.tasks.configure_trust_channel.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.configure_trust_channel.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.configure_trust_channel.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/complaints-channel/about-the-complaint-channel',
        videoUrl:
          'https://drive.google.com/file/d/10vxwRS4sfuRFkViXTVbsyWOnJtp9TpYW/view?usp=sharing',
        mandatory: true,
      },
    ],
  },
  {
    id: 'projects',
    title: i18n.t('growth.get_started.tours_content.modules.projects'),
    moduleName: AvailableModules.projects,
    tasks: [
      {
        taskKey: 'create_project',
        title: i18n.t('growth.get_started.tours_content.tasks.create_project.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.create_project.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.create_project.description'),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/projects/how-to-create-and-assign-projects',
        videoUrl:
          'https://drive.google.com/file/d/1I38T3EL2pKytHPVf2OHapeBtSFNw8van/view?usp=drive_link',
        mandatory: true,
      },
      {
        taskKey: 'explore_project_management',
        title: i18n.t('growth.get_started.tours_content.tasks.explore_project_management.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.explore_project_management.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.explore_project_management.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/projects/about-project-management-tool',
        videoUrl:
          'https://drive.google.com/file/d/1K9ibxI9ylDGUVht8Kva6GkZJp63a7vG-/view?usp=drive_link',
        mandatory: true,
      },
      {
        taskKey: 'view_export_project_data',
        title: i18n.t('growth.get_started.tours_content.tasks.view_export_project_data.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.view_export_project_data.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.view_export_project_data.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/projects/how-to-view-and-export-the-hours-dedicated-to-each-project',
        videoUrl:
          'https://drive.google.com/file/d/1fyvetXGZ6ljbpONsVWeT7oOub-Ofq4H0/view?usp=drive_link',
        mandatory: false,
      },
      {
        taskKey: 'flexible_project_tracking',
        title: i18n.t('growth.get_started.tours_content.tasks.flexible_project_tracking.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.flexible_project_tracking.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.flexible_project_tracking.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/en_US/sobre-el-seguimiento-flexible-de-proyectos',
        videoUrl:
          'https://drive.google.com/file/d/12JL9Mgjzu3OWihu7ZQUTZhDKmUx1JGbx/view?usp=drive_link',
        mandatory: true,
      },
    ],
  },
  {
    id: 'shift_management',
    title: i18n.t('growth.get_started.tours_content.modules.shift_management'),
    moduleName: AvailableModules.shift_management,
    tasks: [
      {
        taskKey: 'create_shifts',
        title: i18n.t('growth.get_started.tours_content.tasks.create_shifts.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.create_shifts.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.create_shifts.description'),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/shift-management/how-to-create-and-assign-shifts',
        videoUrl:
          'https://drive.google.com/file/d/1n6bInwTYU_uKAeTrEV_9VD__ozRVjAg4/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'edit_delete_shifts',
        title: i18n.t('growth.get_started.tours_content.tasks.edit_delete_shifts.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.edit_delete_shifts.ai_question'),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.edit_delete_shifts.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/shift-management/how-to-edit-and-delete-shifts',
        videoUrl:
          'https://drive.google.com/file/d/1-OGHp1aKKOmr4d1IxVwTfvS_YtFtL0Be/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'create_rotating_shifts',
        title: i18n.t('growth.get_started.tours_content.tasks.create_rotating_shifts.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.create_rotating_shifts.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_rotating_shifts.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/shift-management/about-rotating-shifts',
        videoUrl:
          'https://drive.google.com/file/d/1APofyVWt0xhttmd1XrwifQ3GbZYDOuvl/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'configure_saved_shifts',
        title: i18n.t('growth.get_started.tours_content.tasks.configure_saved_shifts.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.configure_saved_shifts.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.configure_saved_shifts.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/en_US/about-saved-shifts',
        videoUrl:
          'https://drive.google.com/file/d/1CTVr0rUK9vdYl64zhdGbQUU21b--Np_C/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'configure_shifts_coverage',
        title: i18n.t('growth.get_started.tours_content.tasks.configure_shifts_coverage.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.configure_shifts_coverage.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.configure_shifts_coverage.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/shift-management/about-coverage',
        videoUrl:
          'https://drive.google.com/file/d/1Y_3y0p-B1ZiZ0Fj_03E1ia6_LTOJ18uI/view?usp=sharing',
        mandatory: false,
      },
      {
        taskKey: 'export_planned_shifts',
        title: i18n.t('growth.get_started.tours_content.tasks.export_planned_shifts.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.export_planned_shifts.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.export_planned_shifts.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/es_ES/how-to-export-planned-shifts',
        videoUrl:
          'https://drive.google.com/file/d/1eOKID9vgBNJi9DYhKl3SHpOpnOOhwJy1/view?usp=sharing',
        mandatory: false,
      },
    ],
  },
  {
    id: 'workflows',
    title: i18n.t('growth.get_started.tours_content.modules.workflows'),
    moduleName: null,
    tasks: [
      {
        taskKey: 'create_workflow',
        title: i18n.t('growth.get_started.tours_content.tasks.create_workflow.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.create_workflow.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.create_workflow.description'),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/workflows-automations/a-propos-des-automatisations-de-flux-de-travail',
        videoUrl:
          'https://drive.google.com/file/d/1chaX89g4V9hcA5_shz1VjPvtalXB38wG/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'explore_onboarding_space',
        title: i18n.t('growth.get_started.tours_content.tasks.explore_onboarding_space.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.explore_onboarding_space.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.explore_onboarding_space.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/applicant-tracking-system/about-the-welcome-space',
        videoUrl:
          'https://drive.google.com/file/d/1NsPwGWPhXfhGbYvFZdOjNv4HCVJ8VSYT/view?usp=drive_link',
        mandatory: false,
      },
    ],
  },
  {
    id: 'expenses',
    title: i18n.t('growth.get_started.tours_content.modules.expenses'),
    moduleName: AvailableModules.expenses,
    tasks: [
      {
        taskKey: 'add_expense',
        title: i18n.t('growth.get_started.tours_content.tasks.add_expense.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.add_expense.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.add_expense.description'),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/para-personas-colaboradoras-enviar-gastos/how-to-create-an-expense-from-the-web-or-mobile-app',
        videoUrl:
          'https://drive.google.com/file/d/1jTnGw3AuvrqO6EfTf504RRFHoJ9IkFP5/view?usp=sharing',
        mandatory: false,
      },
      {
        taskKey: 'setup_expense_approval',
        title: i18n.t('growth.get_started.tours_content.tasks.setup_expense_approval.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.setup_expense_approval.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.setup_expense_approval.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/configuracion-del-modulo-admins-rrhh-finanzas/set-up-approval-flows-for-expenses?from_search=219974745',
        videoUrl:
          'https://drive.google.com/file/d/15bSDogIsUwu1icp2LUWIcdh3DqTGBu5r/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'configure_expense_form',
        title: i18n.t('growth.get_started.tours_content.tasks.configure_expense_form.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.configure_expense_form.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.configure_expense_form.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/configuracion-del-modulo-admins-rrhh-finanzas/configure-expenses-forms-regular-per-diem-mileage?from_search=220065192',
        videoUrl:
          'https://drive.google.com/file/d/10_vbvmnUcNCZBo-A6HtajoFnDC77aB_f/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'creating_stripe_bank_account_and_transferring_funds',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.creating_stripe_bank_account_and_transferring_funds.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.creating_stripe_bank_account_and_transferring_funds.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.creating_stripe_bank_account_and_transferring_funds.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/problemas-comunes-y-soporte/how-to-open-a-factorial-cards-account',
        videoUrl: 'https://drive.google.com/file/d/1nPFw-nGehf4dx0WqS2MuYgMVEdDveoQv/view',
        mandatory: true,
      },
      {
        taskKey: 'requesting_factorial_virtual_cards',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.requesting_factorial_virtual_cards.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.requesting_factorial_virtual_cards.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.requesting_factorial_virtual_cards.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/es_ES/how-to-request-issue-and-assign-factorial-cards',
        videoUrl: 'https://drive.google.com/file/d/1BxVGiniOgvLWraagDtVXOVPbEWU1Bn-0/view',
        mandatory: true,
      },
    ],
  },
  {
    id: 'procurement',
    title: i18n.t('growth.get_started.tours_content.modules.procurement'),
    moduleName: AvailableModules.procurement,
    tasks: [
      {
        taskKey: 'configure_accounts_payable',
        title: i18n.t('growth.get_started.tours_content.tasks.configure_accounts_payable.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.configure_accounts_payable.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.configure_accounts_payable.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/procurement/how-to-manage-bank-accounts-in-factorial',
        videoUrl:
          'https://drive.google.com/file/d/1Kp4wwqUEG0wNKN_dZEaudkLVnF0hWiD-/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'add_and_import_invoices',
        title: i18n.t('growth.get_started.tours_content.tasks.add_and_import_invoices.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.add_and_import_invoices.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.add_and_import_invoices.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/compras/how-to-manage-bank-accounts-in-factorial?from_search=220064989',
        videoUrl:
          'https://drive.google.com/file/d/1bfkgyTG0asV4qUL0bxRx3dCIGhwKp9WT/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'add_bank_accounts',
        title: i18n.t('growth.get_started.tours_content.tasks.add_bank_accounts.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.add_bank_accounts.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.add_bank_accounts.description'),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/banca/connecting-bank-accounts-in-factorial?from_search=220064900',
        videoUrl:
          'https://drive.google.com/file/d/18QFvJtE0kG6819HJOQhUR2Yr3T1g6JhA/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'import_vendors_and_link_accounts',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.import_vendors_and_link_accounts.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.import_vendors_and_link_accounts.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.import_vendors_and_link_accounts.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/es_ES/create-vendors-in-factorial',
        videoUrl:
          'https://drive.google.com/file/d/1BN-L3nD_a8iPkZ3FJK62LROgjiku3XGr/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'pay_invoices_and_reconcile_transactions',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.pay_invoices_and_reconcile_transactions.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.pay_invoices_and_reconcile_transactions.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.pay_invoices_and_reconcile_transactions.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/compras/how-to-manage-bank-accounts-in-factorial?from_search=220064913',
        videoUrl:
          'https://drive.google.com/file/d/1bfkgyTG0asV4qUL0bxRx3dCIGhwKp9WT/view?usp=sharing',
        mandatory: false,
      },
      {
        taskKey: 'chart_of_accounts_and_general_ledger',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.chart_of_accounts_and_general_ledger.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.chart_of_accounts_and_general_ledger.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.chart_of_accounts_and_general_ledger.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/compras/how-to-manage-bank-accounts-in-factorial?from_search=220064989',
        videoUrl:
          'https://drive.google.com/file/d/1cVC2C6L2Saj4EsxzE3ARgT56jgt4IcrT/view?usp=sharing',
        mandatory: false,
      },
    ],
  },
  {
    id: 'recruitment',
    title: i18n.t('growth.get_started.tours_content.modules.recruitment'),
    moduleName: AvailableModules.ats,
    tasks: [
      {
        taskKey: 'create_job_opening',
        title: i18n.t('growth.get_started.tours_content.tasks.create_job_opening.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.create_job_opening.ai_question'),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_job_opening.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/applicant-tracking-system/how-to-create-a-job-opening-and-manage-it',
        videoUrl:
          'https://drive.google.com/file/d/1GR6j1QIDon2EgaXGfQ4AwN3VUAi7WAEj/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'configure_company_page',
        title: i18n.t('growth.get_started.tours_content.tasks.configure_company_page.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.configure_company_page.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.configure_company_page.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/applicant-tracking-system/about-the-company-page',
        videoUrl: 'https://drive.google.com/file/d/1ZcSYrCfWPW9faTbTygZGH_M3PIQ6g186/view',
        mandatory: true,
      },
      {
        taskKey: 'explore_recruitment',
        title: i18n.t('growth.get_started.tours_content.tasks.explore_recruitment.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.explore_recruitment.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.explore_recruitment.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/applicant-tracking-system/about-the-recruitment-functionality',
        videoUrl:
          'https://drive.google.com/file/d/1nzuZ5GDz6iehlL0srsl4DoT1DzMoBahv/view?usp=drive_link',
        mandatory: false,
      },
      {
        taskKey: 'candidate_database',
        title: i18n.t('growth.get_started.tours_content.tasks.candidate_database.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.candidate_database.ai_question'),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.candidate_database.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/software-de-reclutamiento/sobre-la-base-de-datos-del-candidato?from_search=149850957',
        videoUrl:
          'https://drive.google.com/file/d/191vl-llj8i2u_NP96zJVkEJ7HHT_5Hw3/view?usp=sharing',
        mandatory: false,
      },
      {
        taskKey: 'import_job_board_contracts',
        title: i18n.t('growth.get_started.tours_content.tasks.import_job_board_contracts.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.import_job_board_contracts.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.import_job_board_contracts.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/software-de-reclutamiento/sobre-la-funcionalidad-de-reclutamiento?from_search=148308631#cmo-gestionar-la-configuracin-de-reclutamiento-1',
        videoUrl: 'https://drive.google.com/file/d/1jcQd0jXjlB3JIrE95rm28P6a3nRqFuDW/view',
        mandatory: false,
      },
    ],
  },
  {
    id: 'training',
    title: i18n.t('growth.get_started.tours_content.modules.training'),
    moduleName: AvailableModules.trainings,
    tasks: [
      {
        taskKey: 'create_training',
        title: i18n.t('growth.get_started.tours_content.tasks.create_training.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.create_training.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.create_training.description'),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/training/about-the-training-functionality',
        videoUrl:
          'https://drive.google.com/file/d/11Yu3lST9FW8YraEHbIrXJ3086AFv7LdP/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'create_training_session_with_participants',
        title: i18n.t('growth.get_started.tours_content.tasks.create_training_session.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.create_training_session.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_training_session.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/training/about-the-training-functionality',
        videoUrl:
          'https://drive.google.com/file/d/11Yu3lST9FW8YraEHbIrXJ3086AFv7LdP/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'configure_training_approvals',
        title: i18n.t('growth.get_started.tours_content.tasks.configure_training_approvals.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.configure_training_approvals.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.configure_training_approvals.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/training/about-training-requests-and-approval-flows',
        videoUrl:
          'https://drive.google.com/file/d/1hTDQhouylFwgp6udwzqW8fhZtM6A2gO7/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'create_competencies',
        title: i18n.t('growth.get_started.tours_content.tasks.create_competencies.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.create_competencies.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_competencies.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/competencies-goals/about-competencies',
        videoUrl:
          'https://drive.google.com/file/d/1tI6hqVg0RC7kvdvaizSoxmlDFAIGF-7y/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'create_training_categories',
        title: i18n.t('growth.get_started.tours_content.tasks.create_training_categories.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.create_training_categories.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_training_categories.description'
        ),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/en_US/training/about-the-training-functionality',
        videoUrl:
          'https://drive.google.com/file/d/1q31DUvS7xEe5XIBBISBgcgejKC9jNoZu/view?usp=sharing',
        mandatory: true,
      },
    ],
  },
  {
    id: 'performance',
    title: i18n.t('growth.get_started.tours_content.modules.performance'),
    moduleName: 'goals,competencies,review_process',
    tasks: [
      {
        taskKey: 'create_performance_review',
        title: i18n.t('growth.get_started.tours_content.tasks.create_performance_review.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.create_performance_review.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.create_performance_review.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/performance-review/about-performance-reviews',
        videoUrl:
          'https://drive.google.com/file/d/1x6MjEFpC5sA-HNKTN3_2K3UfKV6Yllmw/view?usp=drive_link',
        mandatory: true,
      },
      {
        taskKey: 'assign_competencies_to_positions',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.assign_competencies_to_positions.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.assign_competencies_to_positions.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.assign_competencies_to_positions.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/competencies-goals/about-competencies',
        videoUrl:
          'https://drive.google.com/file/d/1tSPD5LjzPAeWQ9k-Pho5p97m4K7rkp2J/view?usp=drive_link',
        mandatory: true,
      },
      {
        taskKey: 'define_goals',
        title: i18n.t('growth.get_started.tours_content.tasks.define_goals.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.define_goals.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.define_goals.description'),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/competencies-goals/about-the-goals-functionality',
        videoUrl:
          'https://drive.google.com/file/d/1vpYuhOy-FQP-t7kQvGlsnW1qH2ghfK8s/view?usp=drive_link',
        mandatory: true,
      },
    ],
  },
  {
    id: 'engagement',
    title: i18n.t('growth.get_started.tours_content.modules.engagement'),
    moduleName: 'surveys,enps,engagement_insights',
    tasks: [
      {
        taskKey: 'create_survey',
        title: i18n.t('growth.get_started.tours_content.tasks.create_survey.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.create_survey.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.create_survey.description'),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/surveys-enps/about-the-surveys-functionality',
        videoUrl:
          'https://drive.google.com/file/d/1MgZUgCeoEMZOU9Udn30TE_OO0IPP34HG/view?usp=drive_link',
        mandatory: true,
      },
      {
        taskKey: 'configure_enps',
        title: i18n.t('growth.get_started.tours_content.tasks.configure_enps.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.configure_enps.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.configure_enps.description'),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/surveys-enps/about-enps',
        videoUrl:
          'https://drive.google.com/file/d/1Qfw-ARFyWnpqKHA-VvEAii34tKxTuGGw/view?usp=sharing',
        mandatory: true,
      },
      {
        taskKey: 'survey_responses',
        title: i18n.t('growth.get_started.tours_content.tasks.survey_responses.title'),
        aiQuestion: i18n.t('growth.get_started.tours_content.tasks.survey_responses.ai_question'),
        description: i18n.t('growth.get_started.tours_content.tasks.survey_responses.description'),
        tourId: null,
        helpCenterUrl:
          'https://help.factorialhr.com/encuestas-y-eNPS/sobre-las-respuestas-a-las-encuestas',
        videoUrl:
          'https://drive.google.com/file/d/1GOOffNgBOssZupqZ4gRkF4FTxAodgRRe/view?usp=sharing',
        mandatory: false,
      },
    ],
  },
  {
    id: 'benefits',
    title: i18n.t('growth.get_started.tours_content.modules.benefits'),
    moduleName: AvailableModules.benefits,
    tasks: [
      {
        taskKey: 'validating_eligible_employees_and_renewal_contracts',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.validating_eligible_employees_and_renewal_contracts.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.validating_eligible_employees_and_renewal_contracts.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.validating_eligible_employees_and_renewal_contracts.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/es_ES/about-benefits',
        videoUrl:
          'https://drive.google.com/file/d/1hM_xwRcYg2r2HGjF7UcDNYG7p1TuQq7r/view?usp=drive_link',
        mandatory: false,
      },
      {
        taskKey: 'creating_stripe_bank_account_and_transferring_funds',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.creating_stripe_bank_account_and_transferring_funds.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.creating_stripe_bank_account_and_transferring_funds.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.creating_stripe_bank_account_and_transferring_funds.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/es_ES/about-benefits',
        videoUrl: 'https://drive.google.com/file/d/1nPFw-nGehf4dx0WqS2MuYgMVEdDveoQv/view',
        mandatory: true,
      },
      {
        taskKey: 'activating_payroll_module_to_migrate_information',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.activating_payroll_module_to_migrate_information.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.activating_payroll_module_to_migrate_information.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.activating_payroll_module_to_migrate_information.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/es_ES/about-benefits',
        videoUrl:
          'https://drive.google.com/file/d/19Hpz3KEI4h7ck0E-vUmg57Y0IcZR97fw/view?usp=drive_link',
        mandatory: false,
      },
      {
        taskKey: 'requesting_factorial_virtual_cards',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.requesting_factorial_virtual_cards.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.requesting_factorial_virtual_cards.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.requesting_factorial_virtual_cards.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/es_ES/about-benefits',
        videoUrl: 'https://drive.google.com/file/d/1BxVGiniOgvLWraagDtVXOVPbEWU1Bn-0/view',
        mandatory: true,
      },
    ],
  },
  {
    id: 'compensation',
    title: i18n.t('growth.get_started.tours_content.modules.compensation'),
    moduleName: AvailableModules.compensations,
    tasks: [
      {
        taskKey: 'creating_payroll_concepts_and_activating_alerts',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.creating_payroll_concepts_and_activating_alerts.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.creating_payroll_concepts_and_activating_alerts.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.creating_payroll_concepts_and_activating_alerts.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/es_ES/payroll-concepts-in-factorial',
        videoUrl:
          'https://drive.google.com/file/d/1jVNxwTwHpNl7XY0S-LiuZCxlMBOIWmT_/view?usp=drive_link',
        mandatory: true,
      },
      {
        taskKey: 'external_users_to_factorial_professionals',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.external_users_to_factorial_professionals.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.external_users_to_factorial_professionals.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.external_users_to_factorial_professionals.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/es_ES/factorial-professionals',
        videoUrl:
          'https://drive.google.com/file/d/1FUxSe3M9-l7_t411hNU7DpDc7xyMnRuf/view?usp=drive_link',
        mandatory: false,
      },
      {
        taskKey: 'configuring_payroll_incidents',
        title: i18n.t('growth.get_started.tours_content.tasks.configuring_payroll_incidents.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.configuring_payroll_incidents.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.configuring_payroll_incidents.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/es_ES/how-to-manage-payroll-employee-updates',
        videoUrl:
          'https://drive.google.com/file/d/1HjjTSNgZ7iVWg06mlOhPt6ufp-GSrkkK/view?usp=drive_link',
        mandatory: false,
      },
      {
        taskKey: 'mapping_time_and_expense_categories_to_payroll_concepts',
        title: i18n.t(
          'growth.get_started.tours_content.tasks.mapping_time_and_expense_categories_to_payroll_concepts.title'
        ),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.mapping_time_and_expense_categories_to_payroll_concepts.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.mapping_time_and_expense_categories_to_payroll_concepts.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/es_ES/how-to-export-payroll-data',
        videoUrl:
          'https://drive.google.com/file/d/1JNxbTpJodcTS90LRo3W89wXKzRHOqZo8/view?usp=drive_link',
        mandatory: false,
      },
      {
        taskKey: 'importing_payroll_summary',
        title: i18n.t('growth.get_started.tours_content.tasks.importing_payroll_summary.title'),
        aiQuestion: i18n.t(
          'growth.get_started.tours_content.tasks.importing_payroll_summary.ai_question'
        ),
        description: i18n.t(
          'growth.get_started.tours_content.tasks.importing_payroll_summary.description'
        ),
        tourId: null,
        helpCenterUrl: 'https://help.factorialhr.com/es_ES/how-to-export-payroll-data',
        videoUrl:
          'https://drive.google.com/file/d/1Bj-XBY_1TB9vT5EplrtWEWIzQQiseuJR/view?usp=drive_link',
        mandatory: false,
      },
    ],
  },
]
